import { Link } from 'react-router-dom';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { goldPriceSeries } from '@/mocks/marketSeries';
import { tradingSummary } from '@/utils/tradingSummary';
import CountUp from '@/components/base/CountUp';
import Reveal from '@/components/base/Reveal';

const instrumentTags = ['SPOT GOLD', 'USD QUOTED', '24 / 5 SESSIONS'];

const focusPillars = [
  {
    icon: 'ri-focus-3-line',
    title: 'One Instrument',
    desc: 'Every decision is made on XAUUSD alone — nothing else pulls the model away.',
  },
  {
    icon: 'ri-route-line',
    title: 'One Playbook',
    desc: 'A single, disciplined strategy governs each entry, hold, and exit.',
  },
  {
    icon: 'ri-timer-flash-line',
    title: 'Every Session',
    desc: 'The system stays engaged through each active gold trading session.',
  },
];

function GoldStrategy() {
  return (
    <section
      id="gold-strategy"
      className="relative w-full overflow-hidden border-y border-background-200 bg-background-100"
    >
      {/* background gold curve */}
      <div className="absolute inset-0 opacity-[0.35] pointer-events-none">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={goldPriceSeries} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="goldFade" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="oklch(var(--primary-500))" stopOpacity={0.35} />
                <stop offset="100%" stopColor="oklch(var(--primary-500))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="price"
              stroke="oklch(var(--primary-500))"
              strokeWidth={2}
              fill="url(#goldFade)"
              dot={false}
              isAnimationActive
              animationDuration={2200}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="relative w-full px-4 md:px-8 lg:px-12 py-16 md:py-24 max-w-[1440px] mx-auto">
        <Reveal>
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-data tracking-[0.24em] text-primary-600 mb-5">
                SINGLE-MARKET FOCUS
              </p>
              <h2 className="font-heading font-extrabold tracking-tight text-foreground-950 text-[34px] leading-[1.08] sm:text-[46px] lg:text-[54px]">
                Built For One Market.
              </h2>
            </div>
            <div className="flex flex-wrap gap-2 lg:pb-2">
              {instrumentTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center rounded-full border border-background-300 bg-background-50 px-3.5 py-1.5 text-[10px] font-data tracking-[0.18em] text-foreground-600 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <p className="mt-6 font-heading font-extrabold tracking-tighter text-foreground-950/90 text-[64px] leading-none sm:text-[130px] lg:text-[190px] whitespace-nowrap -ml-1">
            {tradingSummary.instrument}
          </p>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <Reveal delay={160}>
            <p className="text-[15px] md:text-base leading-relaxed text-foreground-600 max-w-[540px]">
              Every recorded trade is built on a single instrument: {tradingSummary.instrument}.
              One market, mastered end to end — every decision, every execution, every result.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-accent-300 bg-accent-100/70 px-3.5 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
              <span className="text-[11px] font-data tracking-[0.16em] text-accent-900">
                NO SPLIT FOCUS ACROSS MARKETS
              </span>
            </div>
          </Reveal>

          <Reveal delay={220}>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border border-background-300 bg-background-50 px-5 py-5">
                <p className="font-data text-[32px] md:text-[40px] font-semibold text-foreground-950 leading-none">
                  <CountUp value={tradingSummary.totalTrades} />
                </p>
                <p className="mt-2 text-[12px] text-foreground-500">Recorded Trades</p>
              </div>
              <div className="rounded-lg border border-primary-200 bg-primary-50 px-5 py-5">
                <p className="font-data text-[32px] md:text-[40px] font-semibold text-primary-700 leading-none">
                  <CountUp value={100} suffix="%" />
                </p>
                <p className="mt-2 text-[12px] text-primary-700/80">
                  {tradingSummary.instrument} Focus
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
          {focusPillars.map((pillar, idx) => (
            <Reveal key={pillar.title} delay={idx * 110}>
              <div className="group h-full rounded-lg border border-background-300 bg-background-50 px-5 py-5 flex items-start gap-3.5 transition-all duration-300 ease-out will-change-transform hover:-translate-y-1.5 hover:border-primary-300">
                <span className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0 transition-colors duration-300 group-hover:bg-primary-100">
                  <i className={`${pillar.icon} text-[18px] text-secondary-700`}></i>
                </span>
                <div className="min-w-0">
                  <p className="font-heading font-bold text-[16px] tracking-tight text-foreground-950">
                    {pillar.title}
                  </p>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-foreground-500">
                    {pillar.desc}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={280}>
          <div className="mt-10 pt-6 border-t border-background-300 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Link
              to="/gold-strategy"
              className="inline-flex items-center gap-2 text-[14px] font-semibold text-primary-700 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Explore Gold Strategy
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
            <p className="font-data text-[12px] tracking-wide text-foreground-500">
              {tradingSummary.period}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default GoldStrategy;